import React, { useState, useEffect } from 'react';
import { Plus, Calendar, MapPin, Trash2, Calculator, Edit2, Copy } from 'lucide-react';
import { DVC_RESORTS, getAvailableRoomTypes, getAvailableViewTypes, getAvailableResorts } from '../data/dvcData';
import { getViewName, getViewOptions } from '../data/viewMappings';
import { getBookingWindow, calculateVacationPoints, checkPointAvailability, calculateStayPoints } from '../utils/dvcLogic';
import { format, addDays } from 'date-fns';

const VacationPlanner = ({ contracts, vacations, setVacations }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingVacation, setEditingVacation] = useState(null);
  const [editingStay, setEditingStay] = useState(null);
  const [currentVacation, setCurrentVacation] = useState({
    name: '',
    stays: []
  });
  const [currentStay, setCurrentStay] = useState({
    resort: '',
    roomType: 'STUDIO',
    viewType: 'STANDARD',
    checkIn: '',
    checkOut: '',
    pointsRequired: 0
  });
  const [pointsAnalysis, setPointsAnalysis] = useState(null);
  const [availableRoomTypes, setAvailableRoomTypes] = useState({});
  const [availableViewTypes, setAvailableViewTypes] = useState({});
  const [availableResorts, setAvailableResorts] = useState({});

  useEffect(() => {
    if (vacations.length > 0 && contracts.length > 0) {
      const analysis = checkPointAvailability(contracts, vacations);
      setPointsAnalysis(analysis);
    }
  }, [vacations, contracts]);

  // Load available resorts from JSON on component mount
  useEffect(() => {
    const resorts = getAvailableResorts();
    setAvailableResorts(resorts);
  }, []);

  const calculateStayPointsForForm = () => {
    if (currentStay.resort && currentStay.roomType && currentStay.checkIn && currentStay.checkOut) {
      const checkInDate = new Date(currentStay.checkIn);
      const checkOutDate = new Date(currentStay.checkOut);
      
      if (checkOutDate > checkInDate) {
        const lengthOfStay = Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24));
        
        const stay = {
          resort: currentStay.resort,
          roomType: currentStay.roomType,
          viewType: currentStay.viewType || 'S', // Default to Standard view if not set
          checkIn: currentStay.checkIn,
          lengthOfStay: lengthOfStay,
          year: checkInDate.getFullYear()
        };
        
        console.log('Calculating points for stay:', stay);
        const points = calculateStayPoints(stay);
        console.log('Calculated points:', points);
        setCurrentStay(prev => ({ ...prev, pointsRequired: points }));
        return points;
      } else {
        // Clear points if dates are invalid
        setCurrentStay(prev => ({ ...prev, pointsRequired: 0 }));
        return 0;
      }
    } else {
      // Clear points if required fields are missing
      setCurrentStay(prev => ({ ...prev, pointsRequired: 0 }));
      return 0;
    }
  };

  useEffect(() => {
    calculateStayPointsForForm();
  }, [currentStay.resort, currentStay.roomType, currentStay.viewType, currentStay.checkIn, currentStay.checkOut]);

  // Update available room types when resort changes
  useEffect(() => {
    if (currentStay.resort) {
      const roomTypesArray = getAvailableRoomTypes(currentStay.resort);
      // Convert array to object for dropdown compatibility
      const roomTypes = {};
      roomTypesArray.forEach(type => {
        roomTypes[type] = type;
      });
      setAvailableRoomTypes(roomTypes);
      
      // Reset room type if current selection is not available for this resort
      if (!roomTypes[currentStay.roomType]) {
        const firstAvailableType = roomTypesArray[0] || 'Studio (Sleeps up to 4)';
        setCurrentStay(prev => ({ ...prev, roomType: firstAvailableType }));
      }
    } else {
      setAvailableRoomTypes({});
    }
  }, [currentStay.resort]);

  // Update available view types when resort or room type changes
  useEffect(() => {
    if (currentStay.resort && currentStay.roomType) {
      const viewTypesArray = getAvailableViewTypes(currentStay.resort, currentStay.roomType);
      // Convert array to object for dropdown compatibility
      const viewTypes = {};
      viewTypesArray.forEach(type => {
        viewTypes[type] = type;
      });
      setAvailableViewTypes(viewTypes);
      
      // Reset view type if current selection is not available
      if (!viewTypes[currentStay.viewType]) {
        const firstAvailableView = viewTypesArray[0] || 'S';
        setCurrentStay(prev => ({ ...prev, viewType: firstAvailableView }));
      }
    } else {
      setAvailableViewTypes({ 'S': 'S' });
    }
  }, [currentStay.resort, currentStay.roomType]);

  const addStayToVacation = () => {
    if (!currentStay.resort || !currentStay.checkIn || !currentStay.checkOut) {
      alert('Please fill in all stay details');
      return;
    }

    const checkInDate = new Date(currentStay.checkIn);
    const checkOutDate = new Date(currentStay.checkOut);
    const lengthOfStay = Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24));

    const newStay = {
      ...currentStay,
      lengthOfStay: lengthOfStay,
      year: checkInDate.getFullYear(),
      id: Date.now().toString()
    };

    setCurrentVacation(prev => ({
      ...prev,
      stays: [...prev.stays, newStay]
    }));

    setCurrentStay({
      resort: '',
      roomType: 'STUDIO',
      viewType: 'STANDARD',
      checkIn: '',
      checkOut: '',
      pointsRequired: 0
    });
  };

  const removeStayFromVacation = (stayId) => {
    setCurrentVacation(prev => ({
      ...prev,
      stays: prev.stays.filter(stay => stay.id !== stayId)
    }));
  };

  const editStay = (stay) => {
    setEditingStay(stay.id);
    setCurrentStay({
      resort: stay.resort,
      roomType: stay.roomType,
      viewType: stay.viewType,
      checkIn: stay.checkIn,
      checkOut: stay.checkOut,
      pointsRequired: stay.pointsRequired
    });
  };

  const cancelStayEdit = () => {
    setEditingStay(null);
    setCurrentStay({
      resort: '',
      roomType: 'STUDIO',
      viewType: 'STANDARD',
      checkIn: '',
      checkOut: '',
      pointsRequired: 0
    });
  };

  const duplicateStay = (stay) => {
    // Cancel any current editing
    setEditingStay(null);
    
    // Populate the form with the stay data (without the ID)
    setCurrentStay({
      resort: stay.resort,
      roomType: stay.roomType,
      viewType: stay.viewType,
      checkIn: stay.checkIn,
      checkOut: stay.checkOut,
      pointsRequired: stay.pointsRequired
    });
  };

  const updateStay = () => {
    if (!currentStay.resort || !currentStay.checkIn || !currentStay.checkOut) {
      alert('Please fill in all required fields');
      return;
    }

    const recalculatedPoints = calculateStayPointsForForm();
    console.log('Updating stay with points:', recalculatedPoints);

    const updatedStay = {
      ...currentStay,
      id: editingStay,
      pointsRequired: recalculatedPoints || currentStay.pointsRequired
    };

    console.log('Updated stay object:', updatedStay);

    setCurrentVacation(prev => {
      const updatedVacation = {
        ...prev,
        stays: prev.stays.map(stay => 
          stay.id === editingStay ? updatedStay : stay
        )
      };
      console.log('Updated vacation with new stays:', updatedVacation);
      return updatedVacation;
    });

    cancelStayEdit();
  };

  const saveVacation = () => {
    if (!currentVacation.name || currentVacation.stays.length === 0) {
      alert('Please provide a vacation name and at least one stay');
      return;
    }

    if (editingVacation) {
      // Update existing vacation
      const totalPoints = calculateVacationPoints(currentVacation.stays);
      console.log('Saving vacation with stays:', currentVacation.stays);
      console.log('Calculated total points:', totalPoints);
      
      const updatedVacation = {
        ...currentVacation,
        id: editingVacation,
        totalPoints: totalPoints,
        checkIn: currentVacation.stays[0]?.checkIn
      };

      console.log('Final updated vacation:', updatedVacation);

      setVacations(vacations.map(v => 
        v.id === editingVacation ? updatedVacation : v
      ));
      setEditingVacation(null);
    } else {
      // Create new vacation
      const newVacation = {
        ...currentVacation,
        id: Date.now().toString(),
        totalPoints: calculateVacationPoints(currentVacation.stays),
        checkIn: currentVacation.stays[0]?.checkIn
      };

      setVacations([...vacations, newVacation]);
    }

    setCurrentVacation({ name: '', stays: [] });
    setShowForm(false);
  };

  const deleteVacation = (vacationId) => {
    if (window.confirm('Are you sure you want to delete this vacation?')) {
      setVacations(vacations.filter(v => v.id !== vacationId));
    }
  };

  const editVacation = (vacation) => {
    setEditingVacation(vacation.id);
    setCurrentVacation({
      name: vacation.name,
      stays: [...vacation.stays]
    });
    setShowForm(true);
  };

  const cancelEdit = () => {
    setEditingVacation(null);
    setEditingStay(null);
    setCurrentVacation({
      name: '',
      stays: []
    });
    setCurrentStay({
      resort: '',
      roomType: 'STUDIO',
      viewType: 'STANDARD',
      checkIn: '',
      checkOut: '',
      pointsRequired: 0
    });
    setShowForm(false);
  };

  const getBookingStatus = (stay, contract) => {
    const bookingWindow = getBookingWindow(
      new Date(stay.checkIn),
      contract.homeResort,
      stay.resort
    );
    
    return {
      ...bookingWindow,
      bookingDate: format(bookingWindow.bookingOpenDate, 'MMM dd, yyyy')
    };
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Vacation Planning</h2>
        <button className="btn" onClick={() => setShowForm(true)}>
          <Plus size={16} style={{ marginRight: '8px' }} />
          Plan Vacation
        </button>
      </div>

      {contracts.length === 0 && (
        <div className="alert alert-warning">
          <p>Please add your DVC contracts first to start planning vacations.</p>
        </div>
      )}

      {vacations.length === 0 ? (
        <div className="alert alert-info">
          <p>No vacations planned yet. Start planning your magical Disney vacations!</p>
        </div>
      ) : (
        <div className="grid" style={{ gap: '24px' }}>
          {vacations.map(vacation => (
            <div key={vacation.id} className="card" style={{ margin: 0, border: '1px solid #ddd' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div>
                  <h3 style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
                    <Calendar size={16} style={{ marginRight: '8px', color: '#0066cc' }} />
                    {vacation.name}
                  </h3>
                  <p style={{ margin: '4px 0', color: '#666', fontSize: '14px' }}>
                    {vacation.stays.length} stay{vacation.stays.length !== 1 ? 's' : ''} • {vacation.totalPoints} points required
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button 
                    className="btn btn-secondary" 
                    style={{ padding: '6px 12px', fontSize: '12px' }}
                    onClick={() => editVacation(vacation)}
                  >
                    <Edit2 size={14} />
                  </button>
                  <button 
                    className="btn btn-danger" 
                    style={{ padding: '6px 12px', fontSize: '12px' }}
                    onClick={() => deleteVacation(vacation.id)}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              <div className="grid" style={{ gap: '12px', marginBottom: '16px' }}>
                {vacation.stays.map(stay => (
                  <div key={stay.id} style={{ padding: '12px', background: '#f8f9fa', borderRadius: '6px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: '600', display: 'flex', alignItems: 'center' }}>
                          <MapPin size={14} style={{ marginRight: '6px' }} />
                          {availableResorts[stay.resort]?.name || DVC_RESORTS[stay.resort]?.name || stay.resort}
                        </div>
                        <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                          {stay.roomType} • {getViewName(stay.viewType)} • {format(new Date(stay.checkIn), 'MMM dd')} - {format(new Date(stay.checkOut), 'MMM dd, yyyy')}
                        </div>
                      </div>
                      <div className="points-display" style={{ fontSize: '16px' }}>
                        {stay.pointsRequired}
                      </div>
                    </div>

                    {contracts.length > 0 && (
                      <div style={{ marginTop: '8px', fontSize: '12px' }}>
                        <strong>Booking Windows:</strong>
                        <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {contracts.map(contract => {
                            const status = getBookingStatus(stay, contract);
                            return (
                              <span key={contract.id} className={`badge ${status.canBookNow ? 'badge-success' : 'badge-warning'}`}>
                                {(availableResorts[contract.homeResort]?.name || DVC_RESORTS[contract.homeResort]?.name || contract.homeResort).split(' ')[0]}: {status.bookingDate} {status.isHomeResort ? '(Home)' : '(7mo)'}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {pointsAnalysis && (
                <div>
                  {(() => {
                    const vacResult = pointsAnalysis.vacationResults.find(vr => vr.vacation.id === vacation.id);
                    if (!vacResult) return null;

                    return (
                      <div className={`alert ${vacResult.isFullyFunded ? 'alert-success' : 'alert-warning'}`}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>
                            <strong>Points Status:</strong> {vacResult.pointsAllocated} / {vacResult.pointsNeeded} allocated
                            {vacResult.shortfall > 0 && ` (${vacResult.shortfall} short)`}
                          </span>
                          <Calculator size={16} />
                        </div>
                        {vacResult.allocations.length > 0 && (
                          <div style={{ marginTop: '8px', fontSize: '12px' }}>
                            <strong>Allocation:</strong>
                            {vacResult.allocations.map((alloc, idx) => {
                              const resortName = (availableResorts[alloc.homeResort]?.name || DVC_RESORTS[alloc.homeResort]?.name || alloc.homeResort).split(' ')[0];
                              const breakdown = [];
                              if (alloc.banked > 0) breakdown.push(`${alloc.banked}B`);
                              if (alloc.current > 0) breakdown.push(`${alloc.current}C`);
                              if (alloc.borrowed > 0) breakdown.push(`${alloc.borrowed}Br`);
                              
                              return (
                                <span key={idx} style={{ marginLeft: '8px' }}>
                                  {resortName}: {alloc.totalPoints}pts ({breakdown.join('+')})
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(0,0,0,0.5)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ width: '90%', maxWidth: '800px', margin: 0, maxHeight: '90vh', overflow: 'auto' }}>
            <h3>{editingVacation ? 'Edit Vacation' : 'Plan New Vacation'}</h3>
            
            <div className="form-group">
              <label>Vacation Name *</label>
              <input 
                type="text"
                className="form-control"
                value={currentVacation.name}
                onChange={(e) => setCurrentVacation(prev => ({...prev, name: e.target.value}))}
                placeholder="e.g., Spring Break 2024"
                required
              />
            </div>

            <div style={{ border: '1px solid #ddd', borderRadius: '6px', padding: '16px', marginBottom: '16px' }}>
              <h4>{editingStay ? 'Edit Stay' : 'Add Stay'}</h4>
              
              <div className="grid grid-2">
                <div className="form-group">
                  <label>Resort *</label>
                  <select 
                    className="form-control"
                    value={currentStay.resort}
                    onChange={(e) => setCurrentStay(prev => ({...prev, resort: e.target.value}))}
                  >
                    <option value="">Select a resort...</option>
                    {Object.entries(availableResorts)
                      .sort(([,a], [,b]) => a.name.localeCompare(b.name))
                      .map(([code, resort]) => (
                        <option key={code} value={code}>{resort.name}</option>
                      ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Room Type *</label>
                  <select 
                    className="form-control"
                    value={currentStay.roomType}
                    onChange={(e) => setCurrentStay(prev => ({...prev, roomType: e.target.value}))}
                    disabled={!currentStay.resort}
                  >
                    {!currentStay.resort ? (
                      <option value="">Select a resort first...</option>
                    ) : (
                      Object.entries(availableRoomTypes).map(([code, name]) => (
                        <option key={code} value={code}>{name}</option>
                      ))
                    )}
                  </select>
                </div>

                <div className="form-group">
                  <label>View Type *</label>
                  <select 
                    className="form-control"
                    value={currentStay.viewType}
                    onChange={(e) => setCurrentStay(prev => ({...prev, viewType: e.target.value}))}
                    disabled={!currentStay.resort || !currentStay.roomType}
                  >
                    {Object.keys(availableViewTypes)
                      .sort()
                      .map(viewCode => (
                        <option key={viewCode} value={viewCode}>{getViewName(viewCode)}</option>
                      ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Check-in Date *</label>
                  <input 
                    type="date"
                    className="form-control"
                    value={currentStay.checkIn}
                    onChange={(e) => setCurrentStay(prev => ({...prev, checkIn: e.target.value}))}
                  />
                </div>

                <div className="form-group">
                  <label>Check-out Date *</label>
                  <input 
                    type="date"
                    className="form-control"
                    value={currentStay.checkOut}
                    onChange={(e) => setCurrentStay(prev => ({...prev, checkOut: e.target.value}))}
                    min={currentStay.checkIn}
                  />
                </div>
              </div>

              {currentStay.pointsRequired > 0 && currentStay.checkIn && currentStay.checkOut && (
                <div className="alert alert-info">
                  <strong>Estimated Points:</strong> {currentStay.pointsRequired} points for this stay ({Math.ceil((new Date(currentStay.checkOut) - new Date(currentStay.checkIn)) / (1000 * 60 * 60 * 24))} days)
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px' }}>
                {editingStay && (
                  <button type="button" className="btn btn-secondary" onClick={cancelStayEdit}>
                    Cancel Edit
                  </button>
                )}
                <button 
                  type="button" 
                  className="btn" 
                  onClick={editingStay ? updateStay : addStayToVacation}
                >
                  {editingStay ? 'Update Stay' : 'Add Stay to Vacation'}
                </button>
              </div>
            </div>

            {currentVacation.stays.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <h4>Vacation Stays ({currentVacation.stays.length})</h4>
                {currentVacation.stays.map(stay => (
                  <div key={stay.id} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '8px 12px',
                    background: '#f8f9fa',
                    borderRadius: '4px',
                    marginBottom: '8px'
                  }}>
                    <div>
                      <strong>{availableResorts[stay.resort]?.name || DVC_RESORTS[stay.resort]?.name || stay.resort}</strong> - {stay.roomType} - {getViewName(stay.viewType)}
                      <br />
                      <small>{format(new Date(stay.checkIn), 'MMM dd')} - {format(new Date(stay.checkOut), 'MMM dd, yyyy')} • {stay.pointsRequired} points</small>
                    </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button 
                        className="btn btn-secondary" 
                        style={{ padding: '4px 8px', fontSize: '12px' }}
                        onClick={() => editStay(stay)}
                      >
                        <Edit2 size={12} />
                      </button>
                      <button 
                        className="btn btn-secondary" 
                        style={{ padding: '4px 8px', fontSize: '12px' }}
                        onClick={() => duplicateStay(stay)}
                        title="Duplicate this stay"
                      >
                        <Copy size={12} />
                      </button>
                      <button 
                        className="btn btn-danger" 
                        style={{ padding: '4px 8px', fontSize: '12px' }}
                        onClick={() => removeStayFromVacation(stay.id)}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))}
                <div className="alert alert-info">
                  <strong>Total Points Required:</strong> {calculateVacationPoints(currentVacation.stays)} points
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={editingVacation ? cancelEdit : () => {
                  setCurrentVacation({ name: '', stays: [] });
                  setShowForm(false);
                }}>
                Cancel
              </button>
              <button 
                type="button" 
                className="btn"
                onClick={saveVacation}
                disabled={!currentVacation.name || currentVacation.stays.length === 0}
              >
                {editingVacation ? 'Update Vacation' : 'Save Vacation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VacationPlanner;
